import React, { useEffect, useState } from "react";
import MainLayout from "../../components/MainLayout";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { login } from "../../services/index/users";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from "../../store/reducers/userReducer";
import HCaptcha from "@hcaptcha/react-hcaptcha";

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user);
  const [hCaptchaToken, setHCaptchaToken] = useState("");
  const [hCaptchaEkey, sethCaptchaEkey] = useState("");

  const { mutate, isLoading } = useMutation({
    mutationFn: ({ email, password, hCaptchaToken, hCaptchaEkey }) => {
      return login({ email, password, hCaptchaToken, hCaptchaEkey });
    },
    onSuccess: (data) => {
      dispatch(userActions.setUserInfo(data));
      localStorage.setItem("account", JSON.stringify(data));
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (userState.userInfo) {
      navigate("/");
    }
  }, [navigate, userState.userInfo]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      hCaptchaToken: "",
      hCaptchaEkey: "",
    },
    mode: "onChange",
  });

  const submitHandler = (data) => {
    const { email, password } = data;
    if (!hCaptchaEkey || !hCaptchaToken) {
      console.log("Fill out captcha first");
      return false;
    }
    mutate({ email, password, hCaptchaToken, hCaptchaEkey });
  };

  const handleVerificationSuccess = (token, ekey) => {
    setHCaptchaToken(token);
    sethCaptchaEkey(ekey);
  };

  return (
    <MainLayout>
      <section className="container mx-auto px-5 py-10">
        <div className="mx-auto w-full max-w-sm">
          <h1 className="font-roboto mb-8 text-center text-2xl font-bold text-dark-hard">
            Login
          </h1>
          <form onSubmit={handleSubmit(submitHandler)}>
            <div className="mb-6 flex w-full flex-col">
              <label htmlFor="email" className="font-semibold text-dark-hard">
                Email
              </label>
              <input
                type="email"
                id="email"
                {...register("email", {
                  pattern: {
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: "Invalid email address",
                  },
                  required: {
                    value: true,
                    message: "Email is required",
                  },
                })}
                placeholder="Enter email"
                className={`mt-3 block rounded-lg border px-5 py-4 font-semibold text-dark-hard outline-none placeholder:text-gray-200 ${
                  errors.email ? "border-red-500" : ""
                }`}
              />
              {errors.email?.message && (
                <p className="mt-1 text-xs text-red-500">
                  {errors?.email.message}
                </p>
              )}
            </div>
            <div className=" flex w-full flex-col">
              <label
                htmlFor="password"
                className="font-semibold text-dark-hard"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                {...register("password", {
                  required: {
                    value: true,
                    message: "Password is required",
                  },
                  minLength: {
                    value: 6,
                    message: "Password length must be atleast 6 characters",
                  },
                })}
                placeholder="Enter password"
                className={`mt-3 block rounded-lg border px-5 py-4 font-semibold text-dark-hard outline-none placeholder:text-gray-200 ${
                  errors.password ? "border-red-500" : ""
                }`}
              />
              {errors.password?.message && (
                <p className="mt-1 text-xs text-red-500">
                  {errors?.password.message}
                </p>
              )}
            </div>
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-primary"
            >
              Forgot Password?
            </Link>
            <div className="mt-6 ">
              <HCaptcha
                sitekey={process.env.REACT_APP_HCAPTCHA_SITKEY}
                onVerify={(token, ekey) =>
                  handleVerificationSuccess(token, ekey)
                }
              />
            </div>
            <button
              type="submit"
              disabled={
                !isValid || isLoading || !hCaptchaEkey || !hCaptchaToken
              }
              className="my-6 w-full rounded-lg bg-primary px-8 py-4 text-lg font-bold text-white disabled:cursor-not-allowed disabled:opacity-30"
            >
              Sign In
            </button>
            <p className="text-sm font-semibold text-dark-light">
              Do not have an account?{" "}
              <Link to="/register" className="text-primary">
                Sign up now
              </Link>
            </p>
          </form>
        </div>
      </section>
    </MainLayout>
  );
}
